/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * Resolves the developer field indices in a message definition back to the
 * declarations that gave them meaning.
 *
 * Declarations arrive mid-stream, as ordinary messages: `developer_data_id`
 * claims an index for an application, `field_description` describes one field
 * under that index. Both must have been seen before a data record using them
 * can be read, which the file format guarantees but a truncated file may not.
 */
public class DeveloperDataLookup {
    private val applications = HashMap<Int, DeveloperDataIdMesg>()
    private val descriptions = HashMap<DeveloperDataKey, DeveloperFieldDescription>()

    private val _fieldDescriptions: MutableList<DeveloperFieldDescription> = mutableListOf()

    /** Every declaration seen so far, in the order they were met. */
    public val fieldDescriptions: List<DeveloperFieldDescription> get() = _fieldDescriptions

    /**
     * The lookup key for a field of the application currently holding
     * [developerDataIndex].
     *
     * The index alone is not enough, as [DeveloperDataKey] says: nothing stops
     * two applications in one stream from each claiming index 0 in their own
     * `developer_data_id` message, and reading the second one's records against
     * the first one's declarations would silently mislabel and misscale them.
     * The application UUID is not on the wire in a data record, so it is taken
     * from whichever `developer_data_id` last claimed the index — the same
     * application the declaration was registered under.
     */
    private fun key(developerDataIndex: UByte, fieldNum: UByte): DeveloperDataKey =
        DeveloperDataKey(
            applicationIdOf(developerDataIndex),
            developerDataIndex,
            fieldNum,
        )

    private fun applicationIdOf(developerDataIndex: UByte): List<Byte>? =
        applications[developerDataIndex.toInt()]?.applicationId?.map { (it ?: 0u).toByte() }

    public fun addDeveloperDataId(mesg: DeveloperDataIdMesg) {
        val index = mesg.developerDataIndex ?: return
        applications[index.toInt()] = mesg
    }

    /**
     * Registers a field declaration and returns it, or null when the message is
     * too incomplete to describe anything.
     */
    public fun addFieldDescription(mesg: FieldDescriptionMesg): DeveloperFieldDescription? {
        val index = mesg.developerDataIndex ?: return null
        val fieldNum = mesg.fieldDefinitionNumber ?: return null
        val baseTypeId = mesg.fitBaseTypeId ?: return null
        val baseType = BaseType.fromId(baseTypeId.value) ?: return null

        val application = applications[index.toInt()]

        val description = DeveloperFieldDescription(
            applicationId = application?.applicationId?.let { id -> ByteArray(id.size) { (id[it] ?: 0u).toByte() } },
            applicationVersion = application?.applicationVersion,
            developerDataIndex = index,
            fieldDefinitionNumber = fieldNum,
            fieldName = mesg.fieldName?.firstOrNull() ?: Factory.UNKNOWN_NAME,
            baseType = baseType,
            units = mesg.units?.firstOrNull() ?: "",
            scale = mesg.scale?.toDouble() ?: Fit.FIELD_DEFAULT_SCALE,
            offset = mesg.offset?.toDouble() ?: Fit.FIELD_DEFAULT_OFFSET,
            nativeFieldNum = mesg.nativeFieldNum,
        )

        // Keyed by the description's own identity rather than by a fresh lookup,
        // so the two can never disagree about which application this belongs to.
        descriptions[description.key] = description
        _fieldDescriptions.add(description)
        return description
    }

    public fun getFieldDescription(developerDataIndex: UByte, fieldNum: UByte): DeveloperFieldDescription? =
        descriptions[key(developerDataIndex, fieldNum)]
            // A file that described a field before the `developer_data_id`
            // claiming its index registered it under no application at all. That
            // ordering is not what the format prescribes, but it used to decode,
            // and the declaration is still the only candidate — the lookup above
            // has already ruled out any application having declared this field
            // itself.
            ?: descriptions[DeveloperDataKey(null, developerDataIndex, fieldNum)]

    /** A fresh field for [definition], or null when nothing has declared it. */
    public fun createField(definition: DeveloperFieldDefinition): DeveloperField? =
        getFieldDescription(definition.developerDataIndex, definition.num)?.createField()
}
